import { Toggle } from "../../components/Toggle";
import { t } from "../../i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LoaderCircle,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  UserRound,
  UsersRound,
} from "lucide-react";
import type {
  AgentDefinition,
  AgentType,
  AgentWorkspaceEditPolicy,
  Model,
} from "@lumen/shared";
import { api } from "../../api";
import { ModelSearchPicker } from "../../components/composer/ModelSearchPicker";
import {
  AgentIcon,
  defaultAgentIcon,
} from "../../components/agent-workspaces/AgentIcon";
import { AgentIconPicker } from "../../components/agent-workspaces/AgentIconPicker";
import { AgentWorkspaceEditPolicyEditor } from "../../components/agent-workspaces/AgentWorkspaceEditPolicyEditor";
import { completePersonalizationRequestFromLocation } from "../../personalization";

type Draft = {
  slug: string;
  name: string;
  description: string;
  icon: string | null;
  type: AgentType;
  initialPrompt: string;
  userMessagePrompt: string;
  modelId: string;
  scope: "platform" | "organization" | "personal";
  enabled: boolean;
  allowedSubagentIds: string[];
  workspaceEditPolicy: AgentWorkspaceEditPolicy;
};

const emptyDraft: Draft = {
  slug: "",
  name: "",
  description: "",
  icon: defaultAgentIcon,
  type: "main",
  initialPrompt: "",
  userMessagePrompt: "",
  modelId: "",
  scope: "personal",
  enabled: true,
  allowedSubagentIds: [],
  workspaceEditPolicy: { "*": "deny" },
};

function fromAgent(agent: AgentDefinition): Draft {
  return {
    slug: agent.slug,
    name: agent.name,
    description: agent.description,
    icon: agent.icon,
    type: agent.type,
    initialPrompt: agent.initialPrompt,
    userMessagePrompt: agent.userMessagePrompt,
    modelId: agent.modelId ?? "",
    scope: agent.scope,
    enabled: agent.enabled,
    allowedSubagentIds: agent.allowedSubagentIds,
    workspaceEditPolicy: agent.workspaceEditPolicy,
  };
}

export function AgentsPage({
  canManageOrganization,
  canManagePlatform,
  organizationId,
  canManageSiteOrganization = false,
}: {
  canManageOrganization: boolean;
  canManagePlatform: boolean;
  organizationId?: string;
  canManageSiteOrganization?: boolean;
}) {
  const [agents, setAgents] = useState<AgentDefinition[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [draft, setDraft] = useState<Draft>(() => ({
    ...emptyDraft,
    scope: organizationId ? "organization" : "personal",
  }));
  const [saving, setSaving] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const agentModels = useMemo(
    () =>
      draft.scope !== "personal" && !customizing
        ? models.filter((m) => m.scope === "platform")
        : models,
    [draft.scope, customizing, models],
  );
  const [error, setError] = useState("");
  const contextSuffix = organizationId
    ? `?organizationId=${encodeURIComponent(organizationId)}`
    : "";
  const load = useCallback(async (): Promise<AgentDefinition[]> => {
    const agentRows = await api<AgentDefinition[]>(`/agents${contextSuffix}`);
    setAgents(
      organizationId
        ? agentRows.filter((row) => row.scope === "organization")
        : agentRows,
    );
    return agentRows;
  }, [contextSuffix, organizationId]);
  const loadModels = async () => {
    const modelRows = await api<Model[]>("/models");
    setModels(modelRows);
  };
  useEffect(() => {
    void load().catch((reason) =>
      setError(
        reason instanceof Error ? reason.message : "Could not load agents",
      ),
    );
    void loadModels().catch(() => {});
  }, [load]);
  const selected = agents.find((agent) => agent.id === selectedId);
  const subagents = useMemo(
    () =>
      agents.filter(
        (agent) =>
          agent.type === "subagent" &&
          agent.enabled &&
          (!selected ||
            selected.scope === "personal" ||
            selected.inheritedAllowedSubagentIds.includes(agent.id)),
      ),
    [agents, selected],
  );
  const choose = (agent: AgentDefinition) => {
    setSelectedId(agent.id);
    setDraft(fromAgent(agent));
    setCustomizing(Object.keys(agent.personalOverrides).length > 0);
    setError("");
  };
  const fresh = () => {
    setSelectedId(undefined);
    setDraft({
      ...emptyDraft,
      scope: organizationId ? "organization" : "personal",
    });
    setCustomizing(false);
    setError("");
  };
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const workspaceEditPolicy = Object.fromEntries(
        Object.entries(draft.workspaceEditPolicy).map(([pattern, action]) => {
          const normalizedPattern = pattern.trim();
          if (!normalizedPattern)
            throw new Error(
              "Every workspace permission rule needs a file pattern",
            );
          return [normalizedPattern, action];
        }),
      ) as AgentWorkspaceEditPolicy;
      const body = {
        ...draft,
        workspaceEditPolicy,
        modelId: draft.modelId || null,
        allowedSubagentIds:
          draft.type === "main" ? draft.allowedSubagentIds : [],
      };
      const personal = Boolean(
        selected && selected.scope !== "personal" && customizing,
      );
      const saved = personal
        ? selected!
        : await api<AgentDefinition>(
            `${selectedId ? `/agents/${selectedId}` : "/agents"}${contextSuffix}`,
            {
              method: selectedId ? "PATCH" : "POST",
              body: JSON.stringify(body),
            },
          );
      if (personal)
        await api(`/agents/${selected!.id}/user-settings`, {
          method: "PUT",
          body: JSON.stringify({
            overrides: {
              name: draft.name,
              description: draft.description,
              icon: draft.icon,
              initialPrompt: draft.initialPrompt,
              userMessagePrompt: draft.userMessagePrompt,
              modelId: draft.modelId || null,
              allowedSubagentIds:
                draft.type === "main" ? draft.allowedSubagentIds : undefined,
              workspaceEditPolicy,
            },
          }),
        });
      if (personal)
        await completePersonalizationRequestFromLocation({
          resourceType: "agent",
          resourceId: selected!.id,
        }).catch(() => undefined);
      const rows = await load();
      choose(rows.find((agent) => agent.id === saved.id) ?? saved);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not save agent",
      );
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError("");
    try {
      await api(`/agents/${selectedId}${contextSuffix}`, { method: "DELETE" });
      fresh();
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not delete agent",
      );
    } finally {
      setSaving(false);
    }
  };
  const setUserEnabled = async (enabled: boolean) => {
    if (!selected || selected.scope === "personal") return;
    setSaving(true);
    setError("");
    try {
      await api(`/agents/${selected.id}/user-settings`, {
        method: "PUT",
        body: JSON.stringify({ enabled }),
      });
      const rows = await load();
      choose(
        rows.find((agent) => agent.id === selected.id) ?? {
          ...selected,
          userEnabled: enabled,
        },
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not update agent availability",
      );
    } finally {
      setSaving(false);
    }
  };
  const editGlobal = async () => {
    if (!selected || selected.scope === "personal") return;
    setSaving(true);
    setError("");
    try {
      const global = await api<AgentDefinition>(
        `/agents/${selected.id}/global${contextSuffix}`,
      );
      setDraft(fromAgent(global));
      setCustomizing(false);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not load global agent",
      );
    } finally {
      setSaving(false);
    }
  };
  const editPersonal = async () => {
    if (!selected || selected.scope === "personal") return;
    setSaving(true);
    setError("");
    try {
      const rows = await load();
      const refreshed = rows.find((agent) => agent.id === selected.id);
      if (refreshed) {
        setSelectedId(refreshed.id);
        setDraft(fromAgent(refreshed));
      }
      setCustomizing(true);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not load personal agent",
      );
    } finally {
      setSaving(false);
    }
  };
  const resetSeededAgent = async () => {
    if (!selected?.builtIn) return;
    setSaving(true);
    setError("");
    try {
      const restored = await api<AgentDefinition>(
        `/agents/${selected.id}/reset${contextSuffix}`,
        { method: "POST" },
      );
      const rows = await load();
      choose(rows.find((agent) => agent.id === restored.id) ?? restored);
      setCustomizing(false);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not reset seeded agent",
      );
    } finally {
      setSaving(false);
    }
  };
  const resetPersonalOverrides = async () => {
    if (!selected || selected.scope === "personal") return;
    setSaving(true);
    setError("");
    try {
      await api(`/agents/${selected.id}/user-settings`, {
        method: "PUT",
        body: JSON.stringify({ overrides: {} }),
      });
      const rows = await load();
      const restored = rows.find((agent) => agent.id === selected.id);
      if (restored) choose(restored);
      setCustomizing(false);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not reset personal overrides",
      );
    } finally {
      setSaving(false);
    }
  };
  const editable = !selected || selected.editable;
  const behaviorEditable =
    editable || Boolean(selected?.scope !== "personal" && customizing);
  return (
    <section className="min-w-0 flex-1 overflow-y-auto p-5 md:p-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl bg-lumen-100 text-lumen-700 dark:bg-lumen-900/40 dark:text-lumen-300">
            <UsersRound size={20} />
          </span>
          <div>
            <h1 className="text-2xl font-bold">{t("Agents")}</h1>
            <p className="text-sm text-stone-500">
              {t(
                "Configure main chat personalities and isolated subagents they may delegate work to.",
              )}
            </p>
          </div>
        </div>
        <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
          <div className="panel p-2">
            <button className="button mb-2 w-full" onClick={fresh}>
              <Plus size={16} /> {t("New agent")}
            </button>
            {agents.map((agent) => (
              <button
                key={agent.id}
                onClick={() => choose(agent)}
                className={`w-full rounded-xl p-3 text-left ${!agent.userEnabled ? "opacity-50" : ""} ${selectedId === agent.id ? "bg-lumen-50 dark:bg-lumen-900/30" : "hover:bg-stone-50 dark:hover:bg-white/5"}`}
              >
                <div className="flex items-center gap-2">
                  <AgentIcon
                    name={agent.icon}
                    size={16}
                    className="shrink-0 text-stone-500"
                  />
                  <strong className="min-w-0 flex-1 truncate text-sm">
                    {agent.name}
                  </strong>
                  {!agent.userEnabled && (
                    <span className="text-[10px] text-stone-400 uppercase">
                      {t("off")}
                    </span>
                  )}
                  <span className="text-[10px] text-stone-400 uppercase">
                    {agent.type === "main" ? "main" : "sub"}
                  </span>
                </div>
                <p className="mt-1 truncate text-xs text-stone-500">
                  {agent.description || `/${agent.slug}`}
                </p>
              </button>
            ))}
          </div>
          <div className="panel space-y-5 p-5">
            {selected && selected.scope !== "personal" && (
              <div className="rounded-xl border border-lumen-200 bg-lumen-50 p-3 dark:border-lumen-800 dark:bg-lumen-900/30">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <strong className="text-sm">{t("Agent version")}</strong>
                    <p className="text-xs text-stone-500">
                      {customizing
                        ? t("Editing your personal override")
                        : t("Editing the shared global version")}
                    </p>
                  </div>
                  {Object.keys(selected.personalOverrides).length > 0 && (
                    <span className="rounded-full bg-white/70 px-2 py-1 text-[10px] font-medium tracking-wide text-stone-500 uppercase dark:bg-black/10">
                      {t("Personalized")}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    className={customizing ? "button" : "button-primary"}
                    disabled={saving}
                    onClick={() =>
                      void (customizing ? editGlobal() : undefined)
                    }
                  >
                    {t("Global version")}
                  </button>
                  <button
                    className={customizing ? "button-primary" : "button"}
                    disabled={saving}
                    onClick={() =>
                      void (customizing ? undefined : editPersonal())
                    }
                  >
                    <UserRound size={15} /> {t("Personal version")}
                  </button>
                  {customizing && (
                    <button
                      className="button ml-auto"
                      disabled={saving}
                      onClick={() => void resetPersonalOverrides()}
                    >
                      <RotateCcw size={15} /> {t("Reset to global")}
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <label>
                <span className="label">{t("Name")}</span>
                <input
                  className="field"
                  disabled={!behaviorEditable}
                  value={draft.name}
                  onChange={(event) =>
                    setDraft({ ...draft, name: event.target.value })
                  }
                />
              </label>
              <label>
                <span className="label">{t("Slug")}</span>
                <input
                  className="field font-mono"
                  disabled={
                    !editable || customizing || Boolean(selected?.builtIn)
                  }
                  placeholder="code-review"
                  value={draft.slug}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      slug: event.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9-]/g, "-"),
                    })
                  }
                />
              </label>
            </div>
            <label>
              <span className="label">{t("Description")}</span>
              <input
                className="field"
                disabled={!behaviorEditable}
                value={draft.description}
                onChange={(event) =>
                  setDraft({ ...draft, description: event.target.value })
                }
              />
            </label>
            <div>
              <span className="label">{t("Icon")}</span>
              <AgentIconPicker
                value={draft.icon}
                disabled={!behaviorEditable}
                onChange={(icon) => setDraft({ ...draft, icon })}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label>
                <span className="label">{t("Agent type")}</span>
                <select
                  className="field"
                  disabled={!editable || customizing}
                  value={draft.type}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      type: event.target.value as AgentType,
                      allowedSubagentIds: [],
                    })
                  }
                >
                  <option value="main">{t("Main agent")}</option>
                  <option value="subagent">{t("Subagent")}</option>
                </select>
              </label>
              <div>
                <span className="label">{t("Default model")}</span>
                <ModelSearchPicker
                  models={agentModels}
                  disabled={!behaviorEditable}
                  value={draft.modelId}
                  onChange={(modelId) => setDraft({ ...draft, modelId })}
                  emptyLabel={
                    draft.type === "main"
                      ? "Inherit conversation model"
                      : "Inherit calling agent / conversation"
                  }
                />
              </div>
            </div>
            <label>
              <span className="label">
                {t("Beginning-of-chat system prompt")}
              </span>
              <textarea
                className="field min-h-48 font-mono text-xs leading-5"
                disabled={!behaviorEditable}
                value={draft.initialPrompt}
                onChange={(event) =>
                  setDraft({ ...draft, initialPrompt: event.target.value })
                }
              />
              <span className="mt-1 block text-[11px] text-stone-400">
                {t(
                  "Included with the agent instructions before the conversation history.",
                )}
              </span>
            </label>
            <label>
              <span className="label">
                {t("Additional agent instructions")}
              </span>
              <textarea
                className="field min-h-32 font-mono text-xs leading-5"
                disabled={!behaviorEditable}
                value={draft.userMessagePrompt}
                onChange={(event) =>
                  setDraft({ ...draft, userMessagePrompt: event.target.value })
                }
              />
              <span className="mt-1 block text-[11px] text-stone-400">
                {t(
                  "Included once with the agent instructions before the conversation history.",
                )}
              </span>
            </label>
            <AgentWorkspaceEditPolicyEditor
              value={draft.workspaceEditPolicy}
              disabled={!behaviorEditable}
              onChange={(workspaceEditPolicy) =>
                setDraft({ ...draft, workspaceEditPolicy })
              }
            />
            {draft.type === "main" && (
              <div>
                <span className="label">{t("Allowed subagents")}</span>
                <div className="grid gap-2 rounded-xl border border-stone-200 p-3 sm:grid-cols-2 dark:border-white/10">
                  {subagents.length ? (
                    subagents.map((agent) => (
                      <label
                        key={agent.id}
                        className="flex cursor-pointer items-start gap-2 rounded-lg p-2 hover:bg-stone-50 dark:hover:bg-white/5"
                      >
                        <Toggle
                          className="mt-1"
                          disabled={!behaviorEditable}
                          checked={draft.allowedSubagentIds.includes(agent.id)}
                          onChange={() =>
                            setDraft({
                              ...draft,
                              allowedSubagentIds:
                                draft.allowedSubagentIds.includes(agent.id)
                                  ? draft.allowedSubagentIds.filter(
                                      (id) => id !== agent.id,
                                    )
                                  : [...draft.allowedSubagentIds, agent.id],
                            })
                          }
                        />
                        <span>
                          <strong className="block text-sm">
                            {agent.name}
                          </strong>
                          <span className="block text-xs text-stone-400">
                            {agent.description || `/${agent.slug}`}
                          </span>
                        </span>
                      </label>
                    ))
                  ) : (
                    <p className="col-span-full py-3 text-center text-sm text-stone-400">
                      {t("Create a subagent to enable delegation.")}
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-4">
              {!customizing && (
                <label className="flex items-center gap-2 text-sm">
                  <Toggle
                    disabled={!editable}
                    checked={draft.enabled}
                    onChange={(event) =>
                      setDraft({ ...draft, enabled: event.target.checked })
                    }
                  />{" "}
                  {draft.scope === "platform"
                    ? t("Available site-wide")
                    : draft.scope === "organization"
                      ? t("Available to this organization")
                      : t("Available")}
                </label>
              )}
              {selected && selected.scope !== "personal" && (
                <label className="flex items-center gap-2 text-sm">
                  <Toggle
                    disabled={saving}
                    checked={selected.userEnabled}
                    onChange={(event) =>
                      void setUserEnabled(event.target.checked)
                    }
                  />{" "}
                  {t("Available for me")}
                </label>
              )}
              {editable && !customizing && (
                <label className="flex items-center gap-2 text-sm">
                  <span>{t("Visibility")}</span>
                  <select
                    className="field w-auto py-2"
                    disabled={
                      Boolean(selected?.builtIn) ||
                      (!canManageOrganization &&
                        !canManageSiteOrganization &&
                        draft.scope === "organization") ||
                      (!canManagePlatform && draft.scope === "platform")
                    }
                    value={draft.scope}
                    onChange={(event) => {
                      const scope = event.target.value as Draft["scope"];
                      setDraft({
                        ...draft,
                        scope,
                        ...((scope === "organization" ||
                          scope === "platform") &&
                        draft.modelId &&
                        models.find((m) => m.id === draft.modelId)?.scope ===
                          "personal"
                          ? { modelId: "" }
                          : {}),
                      });
                    }}
                  >
                    <option value="personal">{t("Only me")}</option>
                    {(canManageOrganization || canManageSiteOrganization) && (
                      <option value="organization">
                        {t("This organization")}
                      </option>
                    )}
                    {canManagePlatform && (
                      <option value="platform">{t("Entire site")}</option>
                    )}
                  </select>
                </label>
              )}
              <div className="ml-auto flex gap-2">
                {selected && !selected.builtIn && editable && !customizing && (
                  <button
                    className="button text-red-600"
                    disabled={saving}
                    onClick={() => void remove()}
                  >
                    <Trash2 size={15} /> {t("Delete")}
                  </button>
                )}
                {selected?.builtIn && editable && !customizing && (
                  <button
                    className="button"
                    disabled={saving}
                    onClick={() => void resetSeededAgent()}
                  >
                    <RotateCcw size={15} /> {t("Reset to seeded version")}
                  </button>
                )}
                {behaviorEditable && (
                  <button
                    className="button-primary"
                    disabled={saving || !draft.name || !draft.slug}
                    onClick={() => void save()}
                  >
                    {saving ? (
                      <LoaderCircle className="animate-spin" size={15} />
                    ) : (
                      <Save size={15} />
                    )}{" "}
                    {customizing ? "Save personal version" : "Save agent"}
                  </button>
                )}
              </div>
            </div>
            {error && (
              <pre className="rounded-xl bg-red-50 p-3 text-xs whitespace-pre-wrap text-red-700 dark:bg-red-950/30 dark:text-red-300">
                {error}
              </pre>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
